import React, {useEffect, useMemo, useRef, useState} from "react";
import {Loader, RotateCcw, Save} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {FormSection, SwitchRow, inputClass} from "./SettingsUI";
import Button from "../Common/Button";
import AppleSelect from "../Common/AppleSelect";
import {tabClass} from '../Common/TabBar';

/**
 * Custom WhatsApp confirmation sent to clients after an ONLINE booking
 * (public booking site only — not for appointments created on the tablet or
 * here). Stored on the account (bookingWhatsappTemplateFr / En); the default
 * texts and placeholders come from the API (BookingWhatsappMessage), which
 * also does the real rendering — the preview below mirrors its rules.
 * Also holds the client-cancellation setting (bookingClientCancelEnabled /
 * bookingCancelMinHours): when on, the confirmation e-mail and WhatsApp carry
 * a link ({annulation}) to cancel from the booking site.
 * And the same-day setting (bookingSameDayEnabled): when off, the booking site
 * starts from tomorrow.
 * And the automatic confirmation (bookingAutoConfirmEnabled, which also picks
 * the default text) and the WhatsApp to the chosen employee
 * (bookingEmployeeWhatsappEnabled, number on the employee's form).
 */
const LABELS = {
    prenom: "Prénom",
    nom: "Nom",
    salon: "Salon",
    date: "Date",
    heure: "Heure",
    duree: "Durée",
    prestations: "Prestations",
    employe: "Employé",
    annulation: "Lien d'annulation",
};

const SAMPLE_CANCEL_URL = "https://votre-site.ma/annulation?id=…";

/** Choices for "cancellation possible up to … before the RDV" */
const CANCEL_DELAYS = [0, 2, 6, 12, 24, 48, 72];

const SAMPLE = {
    fr: {prenom: "Yasmine", nom: "Benali", date: "Samedi 10 octobre 2026", heure: "18:00", duree: "1h45", prestations: "Brushing, Soin visage", employe: "Sylvia"},
    en: {prenom: "Yasmine", nom: "Benali", date: "Saturday, October 10, 2026", heure: "6:00 PM", duree: "1h45", prestations: "Brushing, Facial", employe: "Sylvia"},
};

const LANGS = [
    {key: "fr", label: "Français", field: "bookingWhatsappTemplateFr"},
    {key: "en", label: "English", field: "bookingWhatsappTemplateEn"},
];

/** Same rules as BookingWhatsappMessage::render(): unknown {x} kept, lines with an empty placeholder dropped */
function renderPreview(text, values, placeholders) {
    const lines = [];
    for (const line of text.split(/\r?\n/)) {
        let missing = false;
        const out = line.replace(/\{(\w+)\}/g, (m, key) => {
            const k = key.toLowerCase();
            if (!placeholders.includes(k)) return m;
            const v = (values[k] || "").trim();
            if (!v) missing = true;
            return v;
        });
        if (!missing) lines.push(out);
    }
    return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/** WhatsApp *bold* / _italic_ in the preview bubble */
function WhatsAppText({text}) {
    const parts = text.split(/(\*[^*\n]+\*|_[^_\n]+_)/g);
    return parts.map((p, i) =>
        /^\*[^*]+\*$/.test(p) ? <strong key={i}>{p.slice(1, -1)}</strong>
            : /^_[^_]+_$/.test(p) ? <em key={i}>{p.slice(1, -1)}</em>
                : <React.Fragment key={i}>{p}</React.Fragment>
    );
}

/** The API's default texts: an automatically confirmed RDV doesn't promise a confirmation to come */
const defaultTexts = (defaults, autoConfirm) => (autoConfirm && defaults.confirmedTemplates) || defaults.templates;

const BookingWhatsAppTemplate = ({showNotification}) => {
    const [account, setAccount] = useState(null);
    const [defaults, setDefaults] = useState(null);
    const [texts, setTexts] = useState({fr: "", en: ""});
    const [cancelEnabled, setCancelEnabled] = useState(false);
    const [cancelMinHours, setCancelMinHours] = useState(24);
    const [sameDayEnabled, setSameDayEnabled] = useState(false);
    const [autoConfirmEnabled, setAutoConfirmEnabled] = useState(false);
    const [employeeWhatsappEnabled, setEmployeeWhatsappEnabled] = useState(false);
    const [lang, setLang] = useState("fr");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const textareaRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const selected = await secureStorage.getItem("selectedAccount");
                const {data} = await api.get(`/accounts/${selected.id}`, {
                    headers: {"X-account-id": selected.id, "Accept": "application/json"},
                });
                const d = data.bookingWhatsappDefaults;
                if (!d) throw new Error("bookingWhatsappDefaults missing (API not up to date)");
                setAccount({...selected, name: data.name || selected.name});
                setDefaults(d);
                const initial = defaultTexts(d, !!data.bookingAutoConfirmEnabled);
                setTexts({
                    fr: data.bookingWhatsappTemplateFr || initial.fr,
                    en: data.bookingWhatsappTemplateEn || initial.en,
                });
                setCancelEnabled(!!data.bookingClientCancelEnabled);
                setCancelMinHours(data.bookingCancelMinHours ?? 24);
                setSameDayEnabled(!!data.bookingSameDayEnabled);
                setAutoConfirmEnabled(!!data.bookingAutoConfirmEnabled);
                setEmployeeWhatsappEnabled(!!data.bookingEmployeeWhatsappEnabled);
            } catch (e) {
                console.error("Booking WhatsApp template:", e);
                setLoadError(true);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const text = texts[lang];
    const preview = useMemo(
        () => defaults ? renderPreview(text, {
            ...SAMPLE[lang],
            salon: account?.name || "Votre salon",
            annulation: cancelEnabled ? SAMPLE_CANCEL_URL : "",
        }, defaults.placeholders) : "",
        [text, lang, defaults, account, cancelEnabled]
    );

    const insertPlaceholder = (key) => {
        const el = textareaRef.current;
        const token = `{${key}}`;
        const start = el ? el.selectionStart : text.length;
        const end = el ? el.selectionEnd : text.length;
        const next = text.slice(0, start) + token + text.slice(end);
        setTexts(prev => ({...prev, [lang]: next}));
        requestAnimationFrame(() => {
            if (!el) return;
            el.focus();
            el.setSelectionRange(start + token.length, start + token.length);
        });
    };

    /** A text still on the default follows the switch; a custom one is left alone */
    const changeAutoConfirm = (enabled) => {
        const from = defaultTexts(defaults, autoConfirmEnabled);
        const to = defaultTexts(defaults, enabled);
        setTexts(prev => Object.fromEntries(LANGS.map(({key}) => [key, prev[key].trim() === from[key] ? to[key] : prev[key]])));
        setAutoConfirmEnabled(enabled);
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...Object.fromEntries(LANGS.map(l => [l.field, texts[l.key]])),
                bookingClientCancelEnabled: cancelEnabled,
                bookingCancelMinHours: Number(cancelMinHours),
                bookingSameDayEnabled: sameDayEnabled,
                bookingAutoConfirmEnabled: autoConfirmEnabled,
                bookingEmployeeWhatsappEnabled: employeeWhatsappEnabled,
            };
            await api.put(`/accounts/${account.id}`, payload, {
                headers: {"X-account-id": account.id, "Accept": "application/json", "Content-Type": "application/json"},
            });
            showNotification("success", "Message de confirmation enregistré");
        } catch (e) {
            showNotification("error", e.response?.data?.detail || e.response?.data?.message || "Erreur lors de l'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    const title = "Confirmation des réservations en ligne";

    if (loading) {
        return (
            <FormSection title={title}>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <Loader className="w-4 h-4 animate-spin"/> Chargement du message de confirmation…
                </div>
            </FormSection>
        );
    }
    if (loadError || !defaults) {
        return (
            <FormSection title={title}>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Le message de confirmation des réservations en ligne n'est pas disponible pour le moment.
                </p>
            </FormSection>
        );
    }

    const currentDefaults = defaultTexts(defaults, autoConfirmEnabled);
    const isDefault = text.trim() === currentDefaults[lang];
    const tooLong = text.length > defaults.maxLength;

    return (
        <FormSection
            title={title}
            description="Message WhatsApp envoyé automatiquement au client juste après une réservation sur votre site de réservation en ligne, depuis le WhatsApp connecté ci-dessus. Il n'est pas envoyé pour les rendez-vous créés sur la tablette ou le backoffice."
        >
            {/* Language tabs — underlined, as the catalogue tabs */}
            <div>
                <div className="flex border-b border-gray-200 dark:border-gray-700">
                    {LANGS.map(l => (
                        <button
                            key={l.key}
                            type="button"
                            onClick={() => setLang(l.key)}
                            className={`${tabClass(lang === l.key)} -mb-px`}
                        >
                            {l.label}
                        </button>
                    ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Le client reçoit la version de la langue qu'il a choisie sur le site de réservation.
                </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                {/* Editor */}
                <div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {defaults.placeholders.map(key => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => insertPlaceholder(key)}
                                title={`Insérer {${key}}`}
                                className="px-2.5 py-1 rounded-full bg-primary/10 text-xs font-medium text-primary hover:bg-primary/20 transition-colors"
                            >
                                + {LABELS[key] || key}
                            </button>
                        ))}
                    </div>
                    <textarea
                        ref={textareaRef}
                        value={text}
                        onChange={e => setTexts(prev => ({...prev, [lang]: e.target.value}))}
                        rows={14}
                        aria-label="Message de confirmation"
                        className={`${inputClass(tooLong)} !text-sm font-mono leading-relaxed`}
                    />
                    <div className="mt-1 flex justify-between gap-3 text-xs text-gray-500 dark:text-gray-400">
                        <span>*gras* &nbsp;·&nbsp; _italique_ &nbsp;·&nbsp; une ligne dont la variable est vide (ex. sans employé) est retirée</span>
                        <span className={`tabular-nums ${tooLong ? "text-red-500" : ""}`}>{text.length}/{defaults.maxLength}</span>
                    </div>
                </div>

                {/* Preview */}
                <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Aperçu (exemple) :</p>
                    <div className="rounded-lg p-4 min-h-[16rem]" style={{background: "#efeae2"}}>
                        <div className="ml-auto max-w-[90%] rounded-lg rounded-tr-none bg-[#d9fdd3] px-3 py-2 shadow-sm text-[13px] text-gray-900 whitespace-pre-wrap break-words">
                            <WhatsAppText text={preview}/>
                            <div className="text-right text-[10px] text-gray-500 mt-1">18:02 ✓✓</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Automatic confirmation */}
            <div className="space-y-2">
                <SwitchRow
                    id="bookingAutoConfirm"
                    label="Valider automatiquement les rendez-vous"
                    description="Le rendez-vous pris en ligne est confirmé tout de suite, sans validation sur la tablette, et le client reçoit un message de rendez-vous confirmé. Désactivé, il reste « à confirmer » jusqu'à votre validation."
                    checked={autoConfirmEnabled}
                    onChange={changeAutoConfirm}
                />
                {autoConfirmEnabled && !isDefault && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                        Votre message est personnalisé : vérifiez qu'il n'annonce pas une confirmation à venir.
                    </p>
                )}
            </div>

            {/* WhatsApp to the employee */}
            <SwitchRow
                id="bookingEmployeeWhatsapp"
                label="Prévenir l'employé par WhatsApp"
                description="L'employé choisi par le client reçoit le détail du rendez-vous sur le numéro WhatsApp de sa fiche, depuis le WhatsApp connecté ci-dessus. Aucun message si le client n'a pas de préférence ou si la fiche n'a pas de numéro."
                checked={employeeWhatsappEnabled}
                onChange={setEmployeeWhatsappEnabled}
            />

            {/* Same-day bookings */}
            <SwitchRow
                id="bookingSameDay"
                label="Autoriser les réservations le jour même"
                description="Le client peut réserver un créneau encore libre aujourd'hui (les horaires déjà passés restent indisponibles). Désactivé, le site de réservation commence au lendemain."
                checked={sameDayEnabled}
                onChange={setSameDayEnabled}
            />

            {/* Client cancellation */}
            <div className="space-y-3">
                <SwitchRow
                    id="bookingClientCancel"
                    label="Autoriser le client à annuler son rendez-vous"
                    description={`Ajoute un lien d'annulation dans l'e-mail et le WhatsApp de confirmation. Le créneau est libéré et le rendez-vous passe en « annulé » sur la tablette. Si votre message ne contient pas {annulation}, le lien est ajouté à la fin.`}
                    checked={cancelEnabled}
                    onChange={setCancelEnabled}
                />
                {cancelEnabled && (
                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <span>Annulation possible jusqu'à</span>
                        <AppleSelect
                            size="sm"
                            value={cancelMinHours}
                            onChange={e => setCancelMinHours(Number(e.target.value))}
                            aria-label="Délai minimum d'annulation"
                            options={[...new Set([...CANCEL_DELAYS, cancelMinHours])].sort((a, b) => a - b).map(h => ({
                                value: h,
                                label: h === 0 ? "l'heure du rendez-vous" : `${h} h avant`,
                            }))}
                        />
                        {cancelMinHours > 0 && <span>le rendez-vous</span>}
                    </div>
                )}
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
                <Button variant="ghost" icon={RotateCcw} disabled={isDefault}
                        onClick={() => setTexts(prev => ({...prev, [lang]: currentDefaults[lang]}))}>
                    Rétablir le message par défaut
                </Button>
                <Button variant="primary" icon={Save} loading={saving} disabled={tooLong} onClick={save}>
                    Enregistrer le message
                </Button>
            </div>
        </FormSection>
    );
};

export default BookingWhatsAppTemplate;
